import React, { useEffect, useMemo, useState } from "react";
import Dashboard from "../components/Dashboard";
import {
  Bell,
  CalendarClock,
  CircleAlert,
  CreditCard,
  Download,
  Files,
  Gauge,
  Headphones,
  LayoutDashboard,
  Menu,
  Mic,
  Moon,
  Phone,
  PhoneCall,
  PhoneForwarded,
  Play,
  RadioTower,
  Search,
  Settings,
  ShieldCheck,
  Sun,
  UsersRound,
  Wallet,
  Waypoints,
  X,
} from "lucide-react";

const brandLogo = `${process.env.PUBLIC_URL || ""}/callmafia-logo.png`;

const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "extensions", label: "Extensions", icon: PhoneCall },
  { key: "dids", label: "DID Numbers", icon: PhoneForwarded },
  { key: "queues", label: "Queues / Ring Groups", icon: UsersRound },
  { key: "trunks", label: "Trunks", icon: RadioTower },
  { key: "billing", label: "Billing", icon: Wallet },
  { key: "cdr", label: "Call Logs (CDR)", icon: Files },
  { key: "recordings", label: "Recordings", icon: Mic },
  { key: "live", label: "Live Calls", icon: Headphones },
  { key: "settings", label: "Settings", icon: Settings },
];

const STATS = [
  { label: "Active Calls", value: "184", delta: "+12", tone: "primary" },
  { label: "Total Calls Today", value: "9,248", delta: "+7.2%", tone: "success" },
  { label: "Revenue Today", value: "$4,870", delta: "+$560", tone: "warning" },
  { label: "ASR / ACD", value: "78% / 3m 42s", delta: "Stable", tone: "success" },
  { label: "Online Extensions", value: "241 / 268", delta: "89.9%", tone: "primary" },
];

const CALL_TREND = [42, 58, 53, 76, 94, 88, 120, 110, 146, 132, 154, 168];

const EXTENSIONS = [
  { ext: "201180", name: "Sales Desk", type: "WebRTC", status: "Online", did: "877-253-9834" },
  { ext: "706999", name: "Retention", type: "SIP UDP", status: "Offline", did: "844-520-2941" },
  { ext: "979464", name: "After Hours", type: "WebRTC", status: "Online", did: "877-253-9834" },
  { ext: "525548", name: "VIP Forwarding", type: "SIP UDP", status: "Warning", did: "877-253-9834" },
];

const DID_ROWS = [
  { did: "877-253-9834", route: "Queue 2", greeting: "sales-main.wav", fallback: "+1 917 555 0182", owner: "Alpha Reseller" },
  { did: "844-520-2941", route: "Extension 108697", greeting: "billing.wav", fallback: "+1 646 555 0133", owner: "Direct" },
  { did: "866-410-9921", route: "External Number", greeting: "ivr-support.wav", fallback: "+1 408 555 0194", owner: "Reseller B" },
];

const QUEUES = [
  { name: "Queue 2", strategy: "ringall", members: 3, validation: "1 broken endpoint", sla: "91%" },
  { name: "Retention", strategy: "leastrecent", members: 6, validation: "Healthy", sla: "95%" },
  { name: "Night Shift", strategy: "ringall", members: 4, validation: "Healthy", sla: "88%" },
];

const TRUNKS = [
  { name: "Flowroute Primary", auth: "IP + registration", cps: "18", state: "Up" },
  { name: "Twilio Backup", auth: "Credential", cps: "12", state: "Up" },
  { name: "Emergency Carrier", auth: "IP only", cps: "4", state: "Warm standby" },
];

const BILLING_ROWS = [
  { bucket: "DID Revenue", amount: "$2,140", note: "42 active numbers" },
  { bucket: "Usage Charges", amount: "$1,560", note: "US/CA traffic" },
  { bucket: "Reseller Split", amount: "$780", note: "8 reseller tenants" },
  { bucket: "Pending Recharge", amount: "$390", note: "4 open requests" },
];

const CDR_ROWS = [
  { caller: "+18772539834", destination: "Queue 2", duration: "04:32", disposition: "ANSWERED", cost: "$0.21" },
  { caller: "+18445202941", destination: "108697", duration: "00:47", disposition: "NO ANSWER", cost: "$0.04" },
  { caller: "+16175550112", destination: "+16465550133", duration: "12:18", disposition: "ANSWERED", cost: "$0.88" },
  { caller: "+19175550182", destination: "IVR sales-main", duration: "01:05", disposition: "BUSY", cost: "$0.02" },
];

const RECORDINGS = [
  { file: "sales-2026-04-30-1001.wav", ext: "201180", duration: "04:32", size: "2.4 MB" },
  { file: "retention-2026-04-30-1022.wav", ext: "706999", duration: "12:18", size: "6.7 MB" },
  { file: "billing-2026-04-30-1114.wav", ext: "108697", duration: "01:05", size: "780 KB" },
];

const LIVE_CALLS = [
  { caller: "+1 877 253 9834", destination: "Queue 2", duration: "02:42", agent: "201180", state: "Talking" },
  { caller: "+1 844 520 2941", destination: "108697", duration: "00:23", agent: "979464", state: "Ringing" },
  { caller: "+1 646 555 0144", destination: "External Forward", duration: "06:54", agent: "Trunk Backup", state: "Whisper ready" },
];

const NOTIFICATIONS = [
  { title: "Queue validation flagged 1 broken member", note: "Queue 2 contains PJSIP/706999 without active registration.", tone: "warning" },
  { title: "AMI locked to localhost in target architecture", note: "Use Node worker on the same host and Redis for fan-out.", tone: "primary" },
  { title: "Billing alert threshold reached", note: "Reseller B wallet fell below auto-top-up minimum.", tone: "danger" },
];

const SETTINGS_SECTIONS = [
  { title: "Security", text: "JWT with role scopes, IP allow-listing, MFA for admin, and AMI bound to 127.0.0.1." },
  { title: "Realtime", text: "Redis streams for live calls, extension presence, queue health, and wallet snapshot caching." },
  { title: "Telephony", text: "Realtime PJSIP objects, dialplan lookups from MySQL, and no HTTP calls inside Asterisk." },
];

const toneClasses = {
  primary: "bg-dashboard-primary/10 text-dashboard-primary dark:bg-dashboard-primary/20 dark:text-cyan-100",
  success: "bg-dashboard-success/10 text-dashboard-success dark:bg-dashboard-success/20 dark:text-emerald-200",
  warning: "bg-dashboard-warning/20 text-amber-700 dark:bg-dashboard-warning/25 dark:text-amber-100",
  danger: "bg-dashboard-danger/10 text-dashboard-danger dark:bg-dashboard-danger/20 dark:text-rose-200",
  neutral: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
};

function StatusBadge({ children, tone = "neutral" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${toneClasses[tone]}`}>
      {children}
    </span>
  );
}

function PanelCard({ title, action, children, className = "" }) {
  return (
    <section className={`panel-surface panel-enter p-5 ${className}`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold text-slate-900 dark:text-white">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function StatCard({ label, value, delta, tone }) {
  return (
    <div className="panel-surface panel-enter p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
          <p className="mt-2 font-display text-3xl font-semibold text-slate-900 dark:text-white">{value}</p>
        </div>
        <StatusBadge tone={tone}>{delta}</StatusBadge>
      </div>
    </div>
  );
}

function TrendBars() {
  const maxValue = Math.max(...CALL_TREND);

  return (
    <div className="grid grid-cols-12 items-end gap-2 pt-6">
      {CALL_TREND.map((value, index) => (
        <div key={index} className="flex flex-col items-center gap-2">
          <div
            className="w-full rounded-full bg-gradient-to-t from-dashboard-primary to-dashboard-success"
            style={{ height: `${(value / maxValue) * 180}px` }}
          />
          <span className="text-xs text-slate-400">{index + 1}</span>
        </div>
      ))}
    </div>
  );
}

function MetricRow({ label, value, note }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-900/60">
      <div>
        <p className="font-medium text-slate-900 dark:text-white">{label}</p>
        {note ? <p className="text-sm text-slate-500 dark:text-slate-400">{note}</p> : null}
      </div>
      <span className="font-display text-lg font-semibold text-slate-900 dark:text-white">{value}</span>
    </div>
  );
}

function DialPad() {
  const [number, setNumber] = useState("18005550199");
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

  return (
    <PanelCard
      title="Web Phone"
      action={<StatusBadge tone="success">WebRTC ready</StatusBadge>}
      className="h-full"
    >
      <div className="space-y-4">
        <div className="rounded-2xl bg-slate-950 px-4 py-5 text-center text-2xl tracking-[0.35em] text-white">
          {number}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {keys.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setNumber((current) => `${current}${key}`)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-lg font-semibold text-slate-900 transition hover:-translate-y-0.5 hover:border-dashboard-primary hover:text-dashboard-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              {key}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button type="button" className="rounded-2xl bg-dashboard-success px-4 py-3 font-semibold text-white">
            Call
          </button>
          <button type="button" className="rounded-2xl bg-dashboard-danger px-4 py-3 font-semibold text-white">
            Hangup
          </button>
          <button type="button" className="rounded-2xl border border-slate-200 px-4 py-3 font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200">
            Mute
          </button>
          <button type="button" className="rounded-2xl border border-slate-200 px-4 py-3 font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200">
            Hold
          </button>
          <button type="button" className="col-span-2 rounded-2xl border border-slate-200 px-4 py-3 font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200">
            Send DTMF
          </button>
        </div>
      </div>
    </PanelCard>
  );
}

function DashboardPage() {
  return <Dashboard embedded />;
}

function ExtensionsPage() {
  return (
    <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
      <PanelCard
        title="Extensions"
        action={<button type="button" className="rounded-full bg-dashboard-primary px-4 py-2 text-sm font-semibold text-white">Create extension</button>}
      >
        <div className="space-y-4">
          {EXTENSIONS.map((extension) => (
            <div key={extension.ext} className="grid gap-3 rounded-2xl border border-slate-200/70 p-4 md:grid-cols-[1.1fr_0.7fr_0.7fr_0.8fr_auto] md:items-center dark:border-slate-800">
              <div>
                <p className="font-semibold text-slate-900 dark:text-white">{extension.ext} · {extension.name}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">{extension.did}</p>
              </div>
              <MetricRow label="Type" value={extension.type} />
              <MetricRow label="Assigned DID" value={extension.did} />
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Presence</p>
                <StatusBadge tone={extension.status === "Online" ? "success" : extension.status === "Warning" ? "warning" : "danger"}>
                  {extension.status}
                </StatusBadge>
              </div>
              <button type="button" className="rounded-full border border-dashboard-primary px-4 py-2 text-sm font-semibold text-dashboard-primary transition hover:bg-dashboard-primary hover:text-white">
                Open Web Phone
              </button>
            </div>
          ))}
        </div>
      </PanelCard>

      <DialPad />
    </div>
  );
}

function NumbersPage() {
  return (
    <div className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
      <PanelCard
        title="DID Routing"
        action={<button type="button" className="rounded-full bg-dashboard-primary px-4 py-2 text-sm font-semibold text-white">Buy DID</button>}
      >
        <div className="space-y-4">
          {DID_ROWS.map((row) => (
            <div key={row.did} className="rounded-3xl border border-slate-200/80 p-5 dark:border-slate-800">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-xl font-semibold text-slate-900 dark:text-white">{row.did}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Owner: {row.owner}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <StatusBadge tone="primary">{row.route}</StatusBadge>
                  <StatusBadge tone="neutral">Fallback {row.fallback}</StatusBadge>
                </div>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <MetricRow label="Greeting / IVR" value={row.greeting} />
                <MetricRow label="Forward path" value={row.route} />
                <MetricRow label="Failover" value={row.fallback} />
              </div>
            </div>
          ))}
        </div>
      </PanelCard>

      <div className="space-y-6">
        <PanelCard title="Map DID">
          <div className="space-y-3">
            <MetricRow label="Primary route" value="Extension / Queue / External" />
            <MetricRow label="Greeting first" value="Yes, optional" />
            <MetricRow label="Validation" value="Block broken endpoints" />
          </div>
        </PanelCard>
        <PanelCard title="Greeting / IVR">
          <div className="rounded-3xl border border-dashed border-slate-300 p-5 text-center dark:border-slate-700">
            <p className="font-semibold text-slate-900 dark:text-white">Upload greeting audio</p>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              WAV or MP3, normalized, attached to DID before routing.
            </p>
            <button type="button" className="mt-4 rounded-full border border-dashboard-primary px-4 py-2 text-sm font-semibold text-dashboard-primary">
              Select file
            </button>
          </div>
        </PanelCard>
      </div>
    </div>
  );
}

function QueuesPage() {
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {QUEUES.map((queue) => (
        <PanelCard
          key={queue.name}
          title={queue.name}
          action={<StatusBadge tone={queue.validation === "Healthy" ? "success" : "warning"}>{queue.validation}</StatusBadge>}
        >
          <div className="space-y-3">
            <MetricRow label="Strategy" value={queue.strategy} />
            <MetricRow label="Members" value={String(queue.members)} />
            <MetricRow label="SLA" value={queue.sla} />
            <button type="button" className="w-full rounded-2xl bg-dashboard-primary px-4 py-3 font-semibold text-white">
              Validate members
            </button>
          </div>
        </PanelCard>
      ))}
    </div>
  );
}

function TrunksPage() {
  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_0.85fr]">
      <PanelCard
        title="Outbound Trunks"
        action={<StatusBadge tone="success">3 healthy paths</StatusBadge>}
      >
        <div className="space-y-4">
          {TRUNKS.map((trunk) => (
            <div key={trunk.name} className="rounded-2xl border border-slate-200/70 p-4 dark:border-slate-800">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">{trunk.name}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{trunk.auth}</p>
                </div>
                <StatusBadge tone={trunk.state === "Up" ? "success" : "warning"}>{trunk.state}</StatusBadge>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <MetricRow label="Calls per second" value={trunk.cps} />
                <MetricRow label="Failure policy" value="Auto failover" />
              </div>
            </div>
          ))}
        </div>
      </PanelCard>

      <PanelCard title="High-concurrency profile">
        <div className="space-y-3">
          <MetricRow label="Transport" value="PJSIP realtime" note="UDP + WSS where needed" />
          <MetricRow label="Threading" value="Dedicated RTP and HTTP pools" note="Keep AMI local only" />
          <MetricRow label="Trunk order" value="Primary → backup → emergency" note="No HTTP callbacks in dialplan" />
          <MetricRow label="Media" value="ulaw/alaw first" note="Avoid unnecessary transcoding" />
        </div>
      </PanelCard>
    </div>
  );
}

function BillingPage() {
  return (
    <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
      <PanelCard
        title="Wallet & Margin"
        action={<StatusBadge tone="success">$18,420 available</StatusBadge>}
      >
        <div className="space-y-3">
          <MetricRow label="Auto recharge" value="Enabled" note="Top up below $1,000" />
          <MetricRow label="Cost per minute" value="$0.0090 avg" note="Weighted blended rate" />
          <MetricRow label="Recording surcharge" value="$0.002 / min" note="Applied only when enabled" />
          <MetricRow label="DID monthly base" value="$1.25 / DID" note="Per tenant invoice line" />
        </div>
      </PanelCard>

      <PanelCard title="Revenue Breakdown">
        <div className="space-y-3">
          {BILLING_ROWS.map((row) => (
            <MetricRow key={row.bucket} label={row.bucket} value={row.amount} note={row.note} />
          ))}
        </div>
      </PanelCard>
    </div>
  );
}

function CallLogsPage() {
  return (
    <PanelCard
      title="Recent CDR"
      action={<StatusBadge tone="primary">MySQL persistent</StatusBadge>}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <th className="pb-3 pr-4">Caller</th>
              <th className="pb-3 pr-4">Destination</th>
              <th className="pb-3 pr-4">Duration</th>
              <th className="pb-3 pr-4">Disposition</th>
              <th className="pb-3">Cost</th>
            </tr>
          </thead>
          <tbody>
            {CDR_ROWS.map((row) => (
              <tr key={`${row.caller}-${row.destination}`} className="border-b border-slate-100 last:border-none dark:border-slate-900">
                <td className="py-4 pr-4 font-medium text-slate-900 dark:text-white">{row.caller}</td>
                <td className="py-4 pr-4">{row.destination}</td>
                <td className="py-4 pr-4">{row.duration}</td>
                <td className="py-4 pr-4">
                  <StatusBadge tone={row.disposition === "ANSWERED" ? "success" : row.disposition === "NO ANSWER" ? "warning" : "danger"}>
                    {row.disposition}
                  </StatusBadge>
                </td>
                <td className="py-4 font-semibold text-slate-900 dark:text-white">{row.cost}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PanelCard>
  );
}

function RecordingsPage() {
  return (
    <PanelCard
      title="Call Recordings"
      action={<StatusBadge tone="warning">Storage policy 90 days</StatusBadge>}
    >
      <div className="space-y-3">
        {RECORDINGS.map((recording) => (
          <div key={recording.file} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/70 p-4 dark:border-slate-800">
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">{recording.file}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Extension {recording.ext} · {recording.duration} · {recording.size}</p>
            </div>
            <div className="flex gap-2">
              <button type="button" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold dark:border-slate-700">
                <Play className="h-4 w-4" />
                Play
              </button>
              <button type="button" className="inline-flex items-center gap-2 rounded-full bg-dashboard-primary px-4 py-2 text-sm font-semibold text-white">
                <Download className="h-4 w-4" />
                Download
              </button>
            </div>
          </div>
        ))}
      </div>
    </PanelCard>
  );
}

function LiveCallsPage() {
  return (
    <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
      <PanelCard
        title="Live Calls"
        action={<StatusBadge tone="success">Redis + socket</StatusBadge>}
      >
        <div className="space-y-4">
          {LIVE_CALLS.map((call) => (
            <div key={`${call.caller}-${call.agent}`} className="rounded-3xl border border-slate-200/80 p-5 dark:border-slate-800">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-xl font-semibold text-slate-900 dark:text-white">{call.caller}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{call.destination} · Agent {call.agent}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <StatusBadge tone="primary">{call.duration}</StatusBadge>
                  <StatusBadge tone={call.state === "Talking" ? "success" : call.state === "Ringing" ? "warning" : "primary"}>
                    {call.state}
                  </StatusBadge>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" className="rounded-full border border-dashboard-danger px-4 py-2 text-sm font-semibold text-dashboard-danger">
                  Hangup
                </button>
                <button type="button" className="rounded-full border border-dashboard-primary px-4 py-2 text-sm font-semibold text-dashboard-primary">
                  Whisper
                </button>
                <button type="button" className="rounded-full bg-dashboard-primary px-4 py-2 text-sm font-semibold text-white">
                  Barge
                </button>
              </div>
            </div>
          ))}
        </div>
      </PanelCard>

      <PanelCard title="Supervisor Controls">
        <div className="space-y-3">
          <MetricRow label="Presence source" value="AMI + Redis" note="Node worker emits socket updates" />
          <MetricRow label="Call actions" value="Hangup / whisper / barge" note="Command via AMI or ARI local bridge" />
          <MetricRow label="Latency target" value="< 300 ms UI update" note="Redis pub/sub or streams" />
          <MetricRow label="Audit trail" value="Every action logged" note="Actor, tenant, call id, result" />
        </div>
      </PanelCard>
    </div>
  );
}

function SettingsPage() {
  const [activeTab, setActiveTab] = useState("Security");

  const current = SETTINGS_SECTIONS.find((section) => section.title === activeTab) || SETTINGS_SECTIONS[0];

  return (
    <div className="grid gap-6 xl:grid-cols-[0.7fr_1.3fr]">
      <PanelCard title="Configuration Tabs">
        <div className="flex flex-col gap-3">
          {SETTINGS_SECTIONS.map((section) => (
            <button
              key={section.title}
              type="button"
              onClick={() => setActiveTab(section.title)}
              className={`rounded-2xl px-4 py-3 text-left font-medium transition ${
                activeTab === section.title
                  ? "bg-dashboard-primary text-white"
                  : "bg-slate-50 text-slate-700 dark:bg-slate-900/60 dark:text-slate-200"
              }`}
            >
              {section.title}
            </button>
          ))}
        </div>
      </PanelCard>

      <PanelCard
        title={current.title}
        action={<StatusBadge tone="success">Progressive disclosure</StatusBadge>}
      >
        <div className="space-y-4">
          <p className="text-base leading-7 text-slate-600 dark:text-slate-300">{current.text}</p>
          <div className="grid gap-3 md:grid-cols-2">
            <MetricRow label="Firewall" value="Deny by default" note="Expose only 80/443 and trusted SIP IPs" />
            <MetricRow label="JWT roles" value="admin / reseller / user" note="API never public" />
            <MetricRow label="AMI bind" value="127.0.0.1:5038" note="No wildcard listener" />
            <MetricRow label="Strong auth" value="Random 24+ chars" note="Rotate extension passwords" />
          </div>
        </div>
      </PanelCard>
    </div>
  );
}

const pageMap = {
  dashboard: DashboardPage,
  extensions: ExtensionsPage,
  dids: NumbersPage,
  queues: QueuesPage,
  trunks: TrunksPage,
  billing: BillingPage,
  cdr: CallLogsPage,
  recordings: RecordingsPage,
  live: LiveCallsPage,
  settings: SettingsPage,
};

export default function AdminWorkspace() {
  const [activePage, setActivePage] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("admin-v2-theme") === "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    localStorage.setItem("admin-v2-theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  const ActivePage = useMemo(() => pageMap[activePage] || DashboardPage, [activePage]);
  const selectedNav = NAV_ITEMS.find((item) => item.key === activePage) || NAV_ITEMS[0];

  return (
    <div className="min-h-screen bg-dashboard-bg dark:bg-dashboard-darkBg">
      <div className="flex min-h-screen">
        <aside className={`fixed inset-y-0 left-0 z-40 w-72 transform bg-dashboard-sidebar px-5 py-6 text-white shadow-lift transition duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="overflow-hidden rounded-2xl border border-cyan-500/25 bg-black/40 shadow-[0_12px_30px_rgba(8,56,86,0.28)]">
                <img
                  src={brandLogo}
                  alt="CallMafia logo"
                  className="h-11 w-11 object-cover"
                />
              </div>
              <div>
                <p className="font-display text-2xl font-semibold">CallMafia Admin</p>
                <p className="text-sm text-slate-300">Operations control center</p>
              </div>
            </div>
            <button type="button" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="h-6 w-6" />
            </button>
          </div>

          <nav className="mt-8 space-y-2">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = item.key === activePage;

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setActivePage(item.key);
                    setSidebarOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition ${
                    isActive ? "bg-white/12 text-white" : "text-slate-300 hover:bg-white/6 hover:text-white"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="mt-8 rounded-3xl bg-white/8 p-4">
            <p className="text-sm font-semibold">Platform status</p>
            <div className="mt-3 space-y-2 text-sm text-slate-200">
              <p>CPU buffer: 62%</p>
              <p>Redis queue lag: 14 ms</p>
              <p>Fraud guard: armed</p>
            </div>
          </div>
        </aside>

        <div className="flex-1">
          <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
            <div className="mx-auto flex max-w-[1600px] items-center gap-3 px-4 py-4 sm:px-6">
              <button type="button" className="rounded-2xl border border-slate-200 p-2 lg:hidden dark:border-slate-700" onClick={() => setSidebarOpen(true)}>
                <Menu className="h-5 w-5" />
              </button>

              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="hidden min-w-[240px] items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900 md:flex">
                  <Search className="h-4 w-4 text-slate-400" />
                  <input
                    aria-label="Search"
                    placeholder="Search extensions, DID, queues"
                    className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                  />
                </div>

                <div>
                  <p className="font-display text-xl font-semibold text-slate-900 dark:text-white">{selectedNav.label}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Live routing, billing, and telephony operations</p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <div className="hidden rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 dark:bg-slate-900 dark:text-slate-100 md:block">
                  Wallet $18,420.50
                </div>
                <button type="button" className="rounded-full border border-slate-200 p-3 dark:border-slate-700">
                  <Bell className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDarkMode((current) => !current)}
                  className="rounded-full border border-slate-200 p-3 dark:border-slate-700"
                  aria-label="Toggle dark mode"
                >
                  {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>
                <div className="rounded-full bg-dashboard-primary px-4 py-2 text-sm font-semibold text-white">
                  AM
                </div>
              </div>
            </div>
          </header>

          <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6">
            <div className="mb-6 grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
              <div className="panel-surface panel-enter bg-gradient-to-r from-dashboard-primary to-dashboard-success p-6 text-white">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-sm uppercase tracking-[0.3em] text-white/75">Production-safe preview</p>
                    <h1 className="mt-3 font-display text-3xl font-semibold">CallMafia Admin control panel</h1>
                    <p className="mt-3 max-w-2xl text-sm text-white/85">
                      Built for fast operator decisions: live KPIs first, advanced controls behind focused sections, and realtime state designed for Redis + Asterisk events.
                    </p>
                  </div>
                  <div className="grid gap-3 text-sm sm:grid-cols-2">
                    <div className="rounded-2xl bg-white/15 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Gauge className="h-4 w-4" />
                        Setup latency
                      </div>
                      <p className="mt-2 text-2xl font-semibold">1.8s</p>
                    </div>
                    <div className="rounded-2xl bg-white/15 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4" />
                        Fraud shield
                      </div>
                      <p className="mt-2 text-2xl font-semibold">Enabled</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="panel-surface panel-enter p-6">
                <div className="grid gap-3 sm:grid-cols-2">
                  <MetricRow label="Balance cache" value="Redis" note="5s wallet snapshot" />
                  <MetricRow label="Route source" value="MySQL realtime" note="No dialplan HTTP" />
                  <MetricRow label="Supervisor bus" value="Socket.IO" note="Live calls + presence" />
                  <MetricRow label="Audit mode" value="Strict" note="Action logging enabled" />
                </div>
              </div>
            </div>

            <ActivePage />

            <footer className="mt-8 flex flex-wrap items-center justify-between gap-3 px-1 pb-6 text-sm text-slate-500 dark:text-slate-400">
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center gap-2">
                  <CalendarClock className="h-4 w-4" />
                  Last sync 2026-04-30 17:45 CET
                </span>
                <span className="inline-flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Billing engine ready
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center gap-2">
                  <Waypoints className="h-4 w-4" />
                  Redis live bus
                </span>
                <span className="inline-flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Asterisk control plane
                </span>
              </div>
            </footer>
          </main>
        </div>
      </div>
    </div>
  );
}
